import React, { useEffect, useState, useCallback } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineArrowUpTray,
  HiOutlineBookmark,
  HiOutlineBuildingOffice2,
  HiOutlineCube,
  HiOutlineFolder,
  HiOutlineIdentification,
  HiOutlinePlus,
  HiOutlineUser,
} from "react-icons/hi2";
import type { IconType } from "react-icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logoutUser } from "../../store/common/authSlice";
import {
  fetchImpersonationStatus,
  stopImpersonation,
} from "../../store/superAdmin/impersonationSlice";
import IntroModal from "../utilities/IntroModal";
import { CiLock } from "react-icons/ci";
import {
  SimpleField,
  DropdownField,
  superAdminFields,
  sideAdminFields,
  adminFields,
  CRMFields,
  designerFields,
  userFields,
  adminReadFields,
  labPricing,
  caseLifecycleReport,
  photoRecolour,
} from "../../types/SidebarTypes/casesSidebar";
import {
  canAccessCaseLifecycleReport,
  canAccessLabPricing,
  canAccessPhotoRecolour,
  isAdminLikeEmployee,
} from "../../utils/employeeRoles";
import { canAccessSurgeryBookingAdmin } from "../../utils/surgeryBookingAccess";
import { isUrisBrand, isVelvetUiActive } from "../../utils/brandLogo";
import api from "../../utils/api";

const URIS_NAV_ICONS: Record<string, IconType> = {
  Profile: HiOutlineUser,
  "Upload Case": HiOutlineArrowUpTray,
  "Admin Cases": HiOutlineFolder,
  "My Cases": HiOutlineIdentification,
  "Lab Details": HiOutlineBuildingOffice2,
  "Package Manager": HiOutlineCube,
  "Manual Token": HiOutlinePlus,
  Invoices: HiOutlineBookmark,
};

const urisIconButtonClass = (active: boolean) =>
  `flex h-11 w-11 items-center justify-center rounded-xl transition ${
    active
      ? "bg-[rgba(191,30,46,0.16)] text-[#F06A74]"
      : "text-[#8a888b] hover:bg-[#24252a] hover:text-white"
  }`;

function UrisNavIcon({ name }: { name: string }) {
  const Icon = URIS_NAV_ICONS[name] ?? HiOutlineFolder;
  return <Icon className="h-[22px] w-[22px]" />;
}

const Sidebar: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { isImpersonating } = useAppSelector((state) => state.impersonation);
  const role = user?.role ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const isVelvet = isVelvetUiActive(user, location.pathname);
  const isUris = isUrisBrand(user);
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [surgeryAlertCount, setSurgeryAlertCount] = useState(0);

  // Any single portal approval (Cases, Smile, or Photo Recolour) unlocks the sidebar.
  const isUserProfileIncomplete =
    role === "User" &&
    !user?.verified &&
    !user?.verifySmile &&
    !user?.verifyPhotoRecolour;
  const hasPhotoRecolourOnly =
    role === "User" &&
    !user?.verified &&
    !user?.verifySmile &&
    !!user?.verifyPhotoRecolour;
  const currentPath = location.pathname;

  const toggleSidebar = () => setIsOpen((prev) => !prev);
  const closeSidebar = () => setIsOpen(false);

  const toggleDropdown = (fieldName: string) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleLockClick = () => {
    setShowIntroModal(true);
    closeSidebar();
    navigate("/dashboard/profile");
  };

  const fetchImpersonationStatusCallback = useCallback(() => {
    dispatch(fetchImpersonationStatus());
  }, [dispatch]);

  useEffect(() => {
    fetchImpersonationStatusCallback();
  }, [fetchImpersonationStatusCallback]);

  const canSeeSurgeryAdmin = canAccessSurgeryBookingAdmin(
    role,
    employee?.role,
  );
  const canSeeBookSurgery =
    role === "User" &&
    !!user?.verifySurgeryBooking &&
    String(user?.region || "")
      .trim()
      .toUpperCase() === "UK";

  useEffect(() => {
    if (!canSeeSurgeryAdmin && !canSeeBookSurgery) {
      setSurgeryAlertCount(0);
      return;
    }

    let cancelled = false;

    const loadAlerts = async () => {
      try {
        const res = await api.get("/api/surgery-booking/alerts");
        if (cancelled) return;
        const count = Number(res.data?.data?.activeCount || 0);
        setSurgeryAlertCount(Number.isFinite(count) ? count : 0);
      } catch {
        if (!cancelled) setSurgeryAlertCount(0);
      }
    };

    loadAlerts();
    const timer = window.setInterval(loadAlerts, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [canSeeSurgeryAdmin, canSeeBookSurgery, user?.token, user?.verifySurgeryBooking, user?.region]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Close drawer when resizing to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setIsOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLogout = async () => {
    closeSidebar();
    if (isImpersonating) {
      try {
        await dispatch(stopImpersonation()).unwrap();
        // Hard refresh so session restores as Super Admin
        window.location.href = "/dashboard/profile";
      } catch (error) {
        console.error("Error during impersonation stop or user fetch:", error);
      }
    } else {
      dispatch(logoutUser());
      navigate("/");
    }
  };

  if (
    ![
      "Super Admin",
      "Side Admin",
      "Admin Read",
      "Admin",
      "Operations",
      "Production",
      "User",
    ].includes(role)
  )
    return null;

  let sidebarFields: (SimpleField | DropdownField)[] = [];

  switch (role) {
    case "Super Admin":
      sidebarFields = superAdminFields;
      break;
    case "Side Admin":
      sidebarFields = sideAdminFields;
      break;
    case "Admin":
      sidebarFields = adminFields;
      break;
    case "Admin Read":
      sidebarFields = adminReadFields;
      break;
    case "Operations":
      if (employee && isAdminLikeEmployee(employee.role)) {
        sidebarFields = adminFields;
      } else {
        sidebarFields = CRMFields;
      }
      break;
    case "Production":
      // Admin employees are dual-portal; keep Admin menus on Production.
      if (employee?.role === "Admin") {
        sidebarFields = adminFields;
      } else {
        sidebarFields = designerFields;
      }
      break;
    case "User":
      sidebarFields = userFields;
      break;
    default:
      sidebarFields = [];
  }

  if (employee?.role === "CRM") {
    sidebarFields = sidebarFields.filter(
      (field) =>
        field.fieldName !== "Units" &&
        field.fieldName !== "FZ Units" &&
        field.fieldName !== "Units Ledger" &&
        field.fieldName !== "Marked Units",
    );
  }

  if (canAccessLabPricing(role, employee?.role)) {
    const hasLabPricing = sidebarFields.some(
      (field) => field.fieldName === "Lab Pricing",
    );
    if (!hasLabPricing) {
      const insertAfter = sidebarFields.findIndex(
        (field) => field.fieldName === "Marked Units",
      );
      if (insertAfter >= 0) {
        sidebarFields = [
          ...sidebarFields.slice(0, insertAfter + 1),
          labPricing,
          ...sidebarFields.slice(insertAfter + 1),
        ];
      } else {
        sidebarFields = [...sidebarFields, labPricing];
      }
    }
  } else {
    sidebarFields = sidebarFields.filter(
      (field) => field.fieldName !== "Lab Pricing",
    );
  }

  if (canAccessCaseLifecycleReport(role, employee?.role)) {
    const hasCaseReport = sidebarFields.some(
      (field) => field.fieldName === "Case Report",
    );
    if (!hasCaseReport) {
      const insertAfter = sidebarFields.findIndex(
        (field) => field.fieldName === "Export Data",
      );
      if (insertAfter >= 0) {
        sidebarFields = [
          ...sidebarFields.slice(0, insertAfter + 1),
          caseLifecycleReport,
          ...sidebarFields.slice(insertAfter + 1),
        ];
      } else {
        sidebarFields = [...sidebarFields, caseLifecycleReport];
      }
    }
  } else {
    sidebarFields = sidebarFields.filter(
      (field) => field.fieldName !== "Case Report",
    );
  }

  const recolourFieldNames = [
    "Photo Recolour",
    "Recolour Credits",
    "Recolour Packages",
    "Order History",
  ];
  const canSeeRecolour = canAccessPhotoRecolour(
    role,
    employee?.role,
    user?.verifyPhotoRecolour,
  );

  if (canSeeRecolour) {
    const hasRecolourLink = sidebarFields.some(
      (field) => field.fieldName === "Photo Recolour",
    );
    if (!hasRecolourLink) {
      sidebarFields = [...sidebarFields, photoRecolour];
    }
  } else {
    sidebarFields = sidebarFields.filter(
      (field) => !recolourFieldNames.includes(field.fieldName),
    );
  }

  if (hasPhotoRecolourOnly) {
    sidebarFields = sidebarFields.filter((field) =>
      ["Profile", "Photo Recolour", "Recolour Credits", "Order History"].includes(
        field.fieldName,
      ),
    );
  }

  if (!canSeeSurgeryAdmin) {
    sidebarFields = sidebarFields.filter(
      (field) => field.fieldName !== "Surgery Slots",
    );
  }

  if (!canSeeBookSurgery) {
    sidebarFields = sidebarFields.filter(
      (field) => field.fieldName !== "Book Live Surgery",
    );
  }

  if (surgeryAlertCount > 0) {
    sidebarFields = sidebarFields.map((field) => {
      if (
        field.fieldName === "Surgery Slots" ||
        field.fieldName === "Book Live Surgery"
      ) {
        return {
          ...field,
          isBadge: true,
          badgeCount: surgeryAlertCount,
        } as SimpleField;
      }
      return field;
    });
  }

  if (isUris) {
    sidebarFields = sidebarFields.filter(
      (field) => field.fieldName !== "Invoices",
    );
  }

  if (isUris && role === "Side Admin") {
    const packageField: SimpleField = {
      fieldName: "Package Manager",
      fieldLink: "/dashboard/admin/uris-packages",
      isBadge: false,
      isSubField: false,
    };
    const manualField: SimpleField = {
      fieldName: "Manual Token",
      fieldLink: "/dashboard/admin/uris-tokens",
      isBadge: false,
      isSubField: false,
    };
    sidebarFields = [...sidebarFields, packageField, manualField];
  }

  const handleMobileNavClick = () => {
    if (window.innerWidth < 768) closeSidebar();
  };

  return (
    <>
      {/* Mobile menu button — fixed so it isn't trapped in a narrow column */}
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        className={`md:hidden fixed top-[4.5rem] left-3 z-[60] flex h-11 w-11 items-center justify-center rounded-lg shadow-lg ${
          isUris
            ? "bg-[#141517] text-[#c5c7cc]"
            : isVelvet
              ? "bg-[#E8D9C4] text-[#2C2C2C]"
              : "bg-[#262626] text-white"
        }`}
      >
        {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
      </button>

      {/* Sidebar / drawer */}
      <div
        className={`portal-sidebar fixed top-0 left-0 h-full z-[70] transform flex flex-col overflow-y-auto
          ${
            isUris
              ? "w-[4.75rem] bg-[#141517] text-[#c5c7cc]"
              : isVelvet
                ? "w-[min(80%,18rem)] bg-[#E8D9C4] text-[#2C2C2C] md:w-full"
                : "w-[min(80%,18rem)] bg-[#262626] text-gray-100 md:w-full"
          }
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static md:h-full md:z-auto
          transition-transform duration-300 ease-in-out`}
      >
        <nav
          className={`flex w-full flex-col pb-8 text-sm lg:text-base font-normal ${
            isUris
              ? "mt-6 items-center gap-3 md:mt-4"
              : "mt-20 gap-4 md:mt-4 md:gap-1"
          }`}
        >
          {sidebarFields.map((field) => (
            <div
              key={field.fieldName}
              className={`flex flex-col ${
                isUris ? "w-full items-center" : "w-full items-start"
              }`}
            >
              {field.isSubField ? (
                <>
                  <button
                    type="button"
                    onClick={() => toggleDropdown(field.fieldName)}
                    className={`flex items-center gap-3 pl-8 w-full ${
                      isVelvet
                        ? openDropdowns[field.fieldName]
                          ? "text-[#2C2C2C] font-semibold"
                          : "hover:text-[#8B7355]"
                        : openDropdowns[field.fieldName]
                          ? "text-[#1e99af] font-semibold"
                          : "hover:text-[#2fa9bf]"
                    }`}
                  >
                    {field.fieldName}
                  </button>
                  {openDropdowns[field.fieldName] && (
                    <div className="ml-6 mt-2 flex flex-col gap-2 md:gap-[0.5vw] text-sm md:text-[1vw]">
                      {(field as DropdownField).subFieldList.map((sub) => (
                        <Link
                          key={sub.subFieldName}
                          to={sub.subFieldLink}
                          onClick={handleMobileNavClick}
                          className={`${
                            currentPath === sub.subFieldLink
                              ? isVelvet
                                ? "text-[#2C2C2C] font-semibold"
                                : "text-[#2fa9bf] font-semibold"
                              : isVelvet
                                ? "hover:text-[#8B7355]"
                                : "hover:text-[#41bacf]"
                          }`}
                        >
                          {sub.subFieldName}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={
                    role === "User" &&
                    isUserProfileIncomplete &&
                    field.fieldName !== "Profile"
                      ? "#"
                      : (field as SimpleField).fieldLink
                  }
                  onClick={(e) => {
                    if (
                      role === "User" &&
                      isUserProfileIncomplete &&
                      field.fieldName !== "Profile"
                    ) {
                      e.preventDefault();
                      handleLockClick();
                    } else {
                      handleMobileNavClick();
                    }
                  }}
                  title={field.fieldName}
                  aria-label={field.fieldName}
                  className={`relative flex items-center ${
                    isUris
                      ? "w-full justify-center py-0.5"
                      : `w-full pl-6 py-2 gap-2 uppercase md:gap-[1vw] ${
                          currentPath === (field as SimpleField).fieldLink
                            ? isVelvet
                              ? "text-[#2C2C2C] font-semibold bg-[#D4C4A8] border-l-4 border-[#8B7355]"
                              : "text-[#2fa9bf] font-semibold bg-[#1A3038] border-l-4 border-[#1DA8C6]"
                            : isVelvet
                              ? "hover:bg-[#DCCDB8] hover:text-[#2C2C2C]"
                              : "hover:text-[#3cb6cc]"
                        }`
                  }`}
                >
                  {isUris ? (
                    <span
                      className={urisIconButtonClass(
                        currentPath === (field as SimpleField).fieldLink,
                      )}
                    >
                      <UrisNavIcon name={field.fieldName} />
                    </span>
                  ) : (
                    field.fieldName
                  )}

                  {field.isBadge &&
                    (field as SimpleField).badgeCount !== undefined &&
                    Number((field as SimpleField).badgeCount) > 0 && (
                      <span
                        className="ml-auto mr-3 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none animate-pulse"
                        title="Live surgery soon / in progress"
                      >
                        {(field as SimpleField).badgeCount}
                      </span>
                    )}
                  {role === "User" &&
                    isUserProfileIncomplete &&
                    !field.isSubField &&
                    field.fieldName !== "Profile" && (
                      <button
                        type="button"
                        onClick={handleLockClick}
                        title="Complete Profile First"
                      >
                        <CiLock className="w-5 h-5" />
                      </button>
                    )}
                </Link>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
            className={
              isUris
                ? "flex w-full justify-center py-0.5 text-[#ff8b96] hover:text-white"
                : `flex items-center pl-6 py-2 gap-2 uppercase md:gap-[1vw] cursor-pointer ${
                    isVelvet
                      ? "text-[#A65D4A] hover:text-[#8B4A3A]"
                      : "text-red-400 hover:text-red-600"
                  }`
            }
          >
            {isUris ? (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl hover:bg-[#2a2b31]">
                <HiOutlineArrowRightOnRectangle className="h-[22px] w-[22px]" />
              </span>
            ) : (
              "Logout"
            )}
          </button>

          {isImpersonating ? (
            <div
              className={
                isUris
                  ? "mx-1 rounded-lg border border-[#6b2430] px-1 py-1.5 text-center text-[9px] leading-tight text-[#ff8b96]"
                  : "border-2 border-red-500 text-red-500 p-2 mx-4"
              }
              title={`Impersonate as ${user?.user_name || ""} ${user?.token || ""}`}
            >
              <p>Impersonate as</p>
              <p className={isUris ? "truncate" : ""}>{user?.user_name}</p>
              <p>{user?.token}</p>
            </div>
          ) : null}
        </nav>
      </div>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[65] md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {showIntroModal && (
        <IntroModal onClose={() => setShowIntroModal(false)} />
      )}
    </>
  );
};

export default Sidebar;
